/**
 * Festivos oficiales de España.
 *
 * Cobertura actual:
 *   - Nacionales fijos (9) + Viernes Santo móvil hardcoded por año.
 *   - Autonómicos: día de la comunidad + festividades regionales reconocidas
 *     de fecha fija. NO incluye Jueves Santo/Lunes de Pascua porque su
 *     observancia varía año a año por comunidad — el manager los añade
 *     manualmente al grupo después de importar.
 *
 * Para añadir más años, extiende `GOOD_FRIDAY`. Para añadir festividades
 * a una CCAA, añade entradas en `fixed` dentro del objeto correspondiente.
 */

const FIXED_NATIONAL = [
  { md: "01-01", name: "Año Nuevo" },
  { md: "01-06", name: "Epifanía del Señor (Reyes)" },
  { md: "05-01", name: "Fiesta del Trabajo" },
  { md: "08-15", name: "Asunción de la Virgen" },
  { md: "10-12", name: "Fiesta Nacional de España" },
  { md: "11-01", name: "Todos los Santos" },
  { md: "12-06", name: "Día de la Constitución" },
  { md: "12-08", name: "Inmaculada Concepción" },
  { md: "12-25", name: "Navidad" },
];

// Viernes Santo (móvil) en formato MM-DD por año.
const GOOD_FRIDAY = {
  2024: "03-29",
  2025: "04-18",
  2026: "04-03",
  2027: "03-26",
  2028: "04-14",
  2029: "03-30",
  2030: "04-19",
};

export const SUPPORTED_YEARS = Object.keys(GOOD_FRIDAY)
  .map((y) => Number(y))
  .sort((a, b) => a - b);

/**
 * @returns {{ date: string, name: string }[]}
 */
export function getSpanishNationalHolidays(year) {
  const items = FIXED_NATIONAL.map((h) => ({
    date: `${year}-${h.md}`,
    name: h.name,
  }));
  const goodFriday = GOOD_FRIDAY[year];
  if (goodFriday) {
    items.push({ date: `${year}-${goodFriday}`, name: "Viernes Santo" });
  }
  items.sort((a, b) => a.date.localeCompare(b.date));
  return items;
}

/**
 * Comunidades autónomas y sus festividades regionales de fecha fija.
 */
const COMMUNITIES = [
  { id: "andalucia", name: "Andalucía", fixed: [{ md: "02-28", name: "Día de Andalucía" }] },
  { id: "aragon", name: "Aragón", fixed: [{ md: "04-23", name: "San Jorge · Día de Aragón" }] },
  { id: "asturias", name: "Asturias", fixed: [{ md: "09-08", name: "Día de Asturias" }] },
  { id: "baleares", name: "Illes Balears", fixed: [{ md: "03-01", name: "Día de les Illes Balears" }] },
  { id: "canarias", name: "Canarias", fixed: [{ md: "05-30", name: "Día de Canarias" }] },
  {
    id: "cantabria",
    name: "Cantabria",
    fixed: [
      { md: "07-28", name: "Día de las Instituciones de Cantabria" },
      { md: "09-15", name: "La Bien Aparecida" },
    ],
  },
  { id: "castilla-la-mancha", name: "Castilla-La Mancha", fixed: [{ md: "05-31", name: "Día de Castilla-La Mancha" }] },
  { id: "castilla-y-leon", name: "Castilla y León", fixed: [{ md: "04-23", name: "Día de Castilla y León" }] },
  {
    id: "cataluna",
    name: "Cataluña",
    fixed: [
      { md: "09-11", name: "Diada Nacional de Catalunya" },
      { md: "12-26", name: "Sant Esteve" },
    ],
  },
  { id: "valencia", name: "Comunitat Valenciana", fixed: [{ md: "10-09", name: "Día de la Comunitat Valenciana" }] },
  { id: "extremadura", name: "Extremadura", fixed: [{ md: "09-08", name: "Día de Extremadura" }] },
  {
    id: "galicia",
    name: "Galicia",
    fixed: [
      { md: "05-17", name: "Día das Letras Galegas" },
      { md: "07-25", name: "Día Nacional de Galicia" },
    ],
  },
  { id: "la-rioja", name: "La Rioja", fixed: [{ md: "06-09", name: "Día de La Rioja" }] },
  { id: "madrid", name: "Comunidad de Madrid", fixed: [{ md: "05-02", name: "Día de la Comunidad de Madrid" }] },
  { id: "murcia", name: "Región de Murcia", fixed: [{ md: "06-09", name: "Día de la Región de Murcia" }] },
  { id: "navarra", name: "Navarra", fixed: [{ md: "12-03", name: "San Francisco Javier" }] },
  { id: "pais-vasco", name: "País Vasco / Euskadi", fixed: [{ md: "10-25", name: "Día del País Vasco / Euskadiren Eguna" }] },
  { id: "ceuta", name: "Ceuta", fixed: [{ md: "09-02", name: "Día de Ceuta" }] },
  { id: "melilla", name: "Melilla", fixed: [{ md: "09-17", name: "Día de Melilla" }] },
];

const buildCommunityGetter = (community) => (year) =>
  community.fixed
    .map((h) => ({ date: `${year}-${h.md}`, name: h.name }))
    .sort((a, b) => a.date.localeCompare(b.date));

/**
 * Catálogo declarativo de presets disponibles para el importador.
 * Cada preset: { id, label, kind, group?: 'national'|'regional'|'local', defaultName(year), getHolidays(year) }
 */
export const SPANISH_PRESETS = [
  {
    id: "es-national",
    label: "Festivos nacionales (España)",
    kind: "national",
    group: "national",
    defaultName: (year) => `Festivos nacionales ${year}`,
    getHolidays: getSpanishNationalHolidays,
  },
  ...COMMUNITIES.map((c) => ({
    id: `es-regional-${c.id}`,
    label: c.name,
    kind: "regional",
    group: "regional",
    defaultName: (year) => `Festivos ${c.name} ${year}`,
    getHolidays: buildCommunityGetter(c),
  })),
];
